/**
 * Teaching Page
 * Course information and educational resources
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiBook, FiUsers, FiAward, FiExternalLink, FiLock, FiEdit2 } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import useEditableContent from '../hooks/useEditableContent';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import { COURSE_IDS } from '../config/courses';

const TeachingBlue = () => {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [courseAccess, setCourseAccess] = useState({});
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

    // Fetch achievements with CRUD operations
  const {
    items: achievements,
    loading: achievementsLoading,
    // error: achievementsError, // Unused
    createItem: createAchievement,
    updateItem: updateAchievement,
    deleteItem: deleteAchievement
  } = useEditableContent('teaching', {
    page: 'teaching',
    section: 'achievements'
  });

  // Fetch courses with CRUD operations
  const {
    items: coursesFromDB,
    // loading: coursesLoading, // Unused
    // error: coursesError, // Unused
    createItem: createCourse,
    updateItem: updateCourse,
    deleteItem: deleteCourse
  } = useEditableContent('teaching', {
    page: 'teaching',
    section: 'courses'
  });

  // Fetch philosophy section
  const {
    items: philosophyItems,
    // loading: philosophyLoading, // Unused
    // error: philosophyError, // Unused
    createItem: createPhilosophy,
    updateItem: updatePhilosophy
  } = useEditableContent('teaching', {
    page: 'teaching',
    section: 'philosophy'
  });

  // Fetch resources section
  const {
    items: resourcesItems,
    // loading: resourcesLoading, // Unused
    // error: resourcesError, // Unused
    createItem: createResources,
    updateItem: updateResources
  } = useEditableContent('teaching', {
    page: 'teaching',
    section: 'resources'
  });

  // Get content data with fallbacks
  const philosophy = philosophyItems[0] || {
    title: 'Teaching Philosophy',
    description: 'My approach to teaching combines rigorous academic foundations with real-world industry experience. I believe in hands-on learning, collaborative projects, and preparing students not just with technical skills, but with the critical thinking, leadership, and problem-solving abilities needed to thrive in the ever-evolving technology landscape.'
  };

  const resources = resourcesItems[0] || {
    title: 'Student Resources',
    description: "Looking for course materials, office hours information, or academic advising? Please visit the individual course sites above or reach out via the contact page. I'm always happy to discuss research opportunities, career guidance, and academic support."
  };

  // Transform courses from DB to display format
  // Filter: Show Active and Coming Soon, hide Pending and Inactive
  const visibleCourses = coursesFromDB.filter(course => {
    const status = course.data?.status || course.status || 'Active';
    return status === 'Active' || status === 'Coming Soon';
  });
  
  const courses = visibleCourses.length > 0 ? visibleCourses.map(course => ({
    ...course,
    code: course.data?.code || course.code || '',
    status: course.data?.status || course.status || 'Active',
    topics: course.data?.topics || course.topics || [],
    link: course.data?.url || null // Use 'url' field from database, expose as 'link' for UI
  })) : [
    {
      code: 'ITWS 1100',
      title: 'Introduction to Information Technology and Web Science',
      description: 'Foundational course covering web technologies, data management, and information systems.',
      status: 'Fall 2024',
      topics: ['HTML/CSS', 'JavaScript', 'Web Architecture', 'Data Structures'],
      link: 'itws1100'
    }
  ];

  // State for achievement modals
  const [showAchModal, setShowAchModal] = useState(false);
  const [currentAch, setCurrentAch] = useState(null);
  const [isCreatingAch, setIsCreatingAch] = useState(false);
  const [localAchData, setLocalAchData] = useState({
    title: '',
    description: '',
    data: { icon: 'FiAward' }
  });

  // State for course modals
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);

  // State for philosophy modal
  const [showPhilosophyModal, setShowPhilosophyModal] = useState(false);
  const [philosophyForm, setPhilosophyForm] = useState({ title: '', description: '' });

  // State for resources modal
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [resourcesForm, setResourcesForm] = useState({ title: '', description: '' });

  // State for course form
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    data: { code: '', status: 'Active', topics: [], url: '' }
  });

  // Achievement handlers
  const startCreateAch = () => {
    setCurrentAch(null);
    setIsCreatingAch(true);
    setLocalAchData({
      title: '',
      description: '',
      data: { icon: 'FiAward' }
    });
    setShowAchModal(true);
  };

  const startEditAch = (ach) => {
    setCurrentAch(ach);
    setIsCreatingAch(false);
    setLocalAchData({
      title: ach.title || '',
      description: ach.description || '',
      data: { icon: ach.data?.icon || 'FiAward' }
    });
    setShowAchModal(true);
  };

  const cancelAchEdit = () => {
    setShowAchModal(false);
    setCurrentAch(null);
    setLocalAchData({ title: '', description: '', data: { icon: 'FiAward' } });
  };

  const handleAchDelete = async (achId) => {
    if (window.confirm('Are you sure you want to delete this achievement?')) {
      const result = await deleteAchievement(achId);
      if (result.success) {
        showToast('Achievement deleted successfully', 'success');
      }
    }
  };

  const saveAch = async () => {
    try {
      let result;
      const achData = {
        ...localAchData,
        contentType: 'teaching',
        metadata: {
          page: 'teaching',
          section: 'achievements',
          published: true
        }
      };
      
      if (isCreatingAch) {
        result = await createAchievement(achData);
      } else {
        result = await updateAchievement(currentAch._id, achData);
      }
      
      if (result.success) {
        showToast(
          isCreatingAch ? 'Achievement created successfully' : 'Achievement updated successfully',
          'success'
        );
        setShowAchModal(false);
        setCurrentAch(null);
      }
    } catch (error) {
      console.error('Error saving achievement:', error);
    }
  };

  // Course handlers
  const startCreateCourse = () => {
    setCurrentCourse(null);
    setIsCreatingCourse(true);
    setCourseForm({
      title: '',
      description: '',
      data: { code: '', status: 'Active', topics: [], link: '' }
    });
    setShowCourseModal(true);
  };

  const startEditCourse = (course) => {
    setCurrentCourse(course);
    setIsCreatingCourse(false);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      data: {
        code: course.data?.code || course.code || '',
        status: course.data?.status || course.status || 'Active',
        topics: course.data?.topics || course.topics || [],
        url: course.data?.url || '' // Use 'url' field from database
      }
    });
    setShowCourseModal(true);
  };

  const handleCourseDelete = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      const result = await deleteCourse(courseId);
      if (result.success) {
        showToast('Course deleted successfully', 'success');
      }
    }
  };

  const saveCourse = async () => {
    try {
      let result;
      const courseData = {
        ...courseForm,
        metadata: {
          page: 'teaching',
          section: 'courses'
        },
        // Ensure status is set in data object, default to 'Active'
        data: {
          ...courseForm.data,
          status: courseForm.data?.status || 'Active'
        }
      };
      
      console.log('Saving course data:', courseData);
      console.log('Course form data.url:', courseForm.data?.url);
      
      // If creating OR if currentCourse doesn't have an _id (fallback course)
      if (isCreatingCourse || !currentCourse?._id) {
        result = await createCourse(courseData);
      } else {
        result = await updateCourse(currentCourse._id, courseData);
      }
      
      if (result.success) {
        showToast(
          isCreatingCourse || !currentCourse?._id ? 'Course created successfully' : 'Course updated successfully',
          'success'
        );
        setShowCourseModal(false);
        setCurrentCourse(null);
      } else {
        showToast(result.message || 'Failed to save course', 'error');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      showToast(error.message || 'Failed to save course', 'error');
    }
  };

  // Philosophy handlers
  const openPhilosophyModal = () => {
    setPhilosophyForm({
      title: philosophy.title || '',
      description: philosophy.description || ''
    });
    setShowPhilosophyModal(true);
  };

  const savePhilosophy = async () => {
    try {
      let result;
      if (philosophyItems.length === 0) {
        result = await createPhilosophy(philosophyForm);
      } else {
        result = await updatePhilosophy(philosophy._id, philosophyForm);
      }
      
      if (result.success) {
        showToast('Philosophy updated successfully', 'success');
        setShowPhilosophyModal(false);
      }
    } catch (error) {
      console.error('Error saving philosophy:', error);
    }
  };

  // Resources handlers
  const openResourcesModal = () => {
    setResourcesForm({
      title: resources.title || '',
      description: resources.description || ''
    });
    setShowResourcesModal(true);
  };

  const saveResources = async () => {
    try {
      let result;
      if (resourcesItems.length === 0) {
        result = await createResources(resourcesForm);
      } else {
        result = await updateResources(resources._id, resourcesForm);
      }
      
      if (result.success) {
        showToast('Resources updated successfully', 'success');
        setShowResourcesModal(false);
      }
    } catch (error) {
      console.error('Error saving resources:', error);
    }
  };

  const checkCourseAccess = useCallback(async () => {
    try {
      // Check access for all courses that need external hosting (not RP001)
      const accessChecks = {};
      
      // Always check ITWS1100 (main external course)
      try {
        const itws1100Response = await api.get('/courses/check-access/itws1100');
        accessChecks.itws1100 = itws1100Response.data.hasAccess;
      } catch (error) {
        console.error('Error checking itws1100 access:', error);
        accessChecks.itws1100 = false;
      }
      
      // Check other courses from database if they have a url
      for (const course of coursesFromDB) {
        const courseUrl = course.data?.url;
        if (courseUrl && courseUrl !== 'rp001') {  // Skip RP001, it uses /lessons route
          try {
            const response = await api.get(`/courses/check-access/${courseUrl}`);
            accessChecks[courseUrl] = response.data.hasAccess;
          } catch (error) {
            console.error(`Error checking ${courseUrl} access:`, error);
            accessChecks[courseUrl] = false;
          }
        } else if (courseUrl === 'rp001') {
          // RP001 access is handled differently (always accessible to authenticated users)
          accessChecks.rp001 = true;
        }
      }
      
      setCourseAccess(accessChecks);
    } catch (error) {
      console.error('Error checking course access:', error);
      setCourseAccess({ itws1100: false });
    }
  }, [coursesFromDB]);

  useEffect(() => {
    if (isAuthenticated) {
      checkCourseAccess();
    }
  }, [isAuthenticated, checkCourseAccess]);

  const handleCourseClick = async (e, courseId) => {
    e.preventDefault();
    
    // Check if course link is configured
    if (!courseId) {
      showToast('Course link not configured. Please contact the administrator.', 'error');
      return;
    }
    
    // Check authentication for all courses
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/teaching' } });
      return;
    }

    if (!courseAccess[courseId] && user.role !== 'admin') {
      showToast('You do not have access to this course. Contact your instructor.', 'warning');
      return;
    }

    // Navigate based on course type
    if (courseId === COURSE_IDS.RP001) {
      navigate('/lessons/index');
    } else {
      // Clean up courseId - remove /course/ prefix if already present (defensive coding)
      const cleanCourseId = courseId.replace(/^\/course\//, '');
      
      // Check if course files exist before navigating (for external courses)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${api.defaults.baseURL}/courses/${cleanCourseId}/index.html?token=${token}`, {
          method: 'HEAD',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          // Course files not found - show error modal instead of navigating
          setErrorModal({
            isOpen: true,
            title: 'Course Not Available',
            message: `Course materials for ${cleanCourseId.toUpperCase()} are not available yet. Please check back later or contact your instructor.`
          });
          return;
        }
        
        // Files exist - navigate to course viewer
        navigate(`/course/${cleanCourseId}`);
      } catch (error) {
        console.error('Error checking course availability:', error);
        setErrorModal({
          isOpen: true,
          title: 'Error',
          message: 'Unable to access course materials. Please try again later.'
        });
      }
    }
  };

  // Helper function to convert icon string to component
  const getIconComponent = (iconName) => {
    const icons = {
      FiAward: <FiAward />,
      FiUsers: <FiUsers />,
      FiBook: <FiBook />
    };
    return icons[iconName] || <FiAward />;
  };

  return (
    <TeachingContainer>
      <Container>
        {/* Welcome message for students */}
        {user && user.role === 'student' && (
          <WelcomeMessage
            as={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <WelcomeIcon><FiBook /></WelcomeIcon>
            <WelcomeContent>
              <WelcomeTitle>Welcome, {user.name}!</WelcomeTitle>
              <WelcomeText>Access your enrolled courses below</WelcomeText>
            </WelcomeContent>
          </WelcomeMessage>
        )}

        <HeroSection
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>Teaching & Education</Title>
          <Subtitle>Professor of Practice at Rensselaer Polytechnic Institute</Subtitle>
        </HeroSection>

        {/* Teaching Philosophy */}
        <Section
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <SectionTitleWithButton>
            <SectionTitle>{philosophy.title}</SectionTitle>
            {isAdmin && (
              <EditButton onClick={openPhilosophyModal}>
                <FiEdit2 /> Edit
              </EditButton>
            )}
          </SectionTitleWithButton>
          <PhilosophyText dangerouslySetInnerHTML={{ __html: philosophy.description }} />
        </Section>

        {/* Achievements Grid */}
        <Section>
          <SectionTitleWithButton>
            <SectionTitle>Achievements & Impact</SectionTitle>
            {isAdmin && (
              <AddButton onClick={startCreateAch}>
                + Add Achievement
              </AddButton>
            )}
          </SectionTitleWithButton>
          <AchievementsGrid>
            {achievementsLoading ? (
              <p>Loading achievements...</p>
            ) : achievements.length === 0 ? (
              <p>No achievements yet.</p>
            ) : (
              achievements.map((achievement, index) => (
                <AchievementCard
                  key={achievement._id}
                  as={motion.div}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  {isAdmin && (
                    <CardActions>
                      <EditIconButton onClick={() => startEditAch(achievement)}>
                        <FiEdit2 />
                      </EditIconButton>
                      <DeleteIconButton onClick={() => handleAchDelete(achievement._id)}>
                        <FaTrash />
                      </DeleteIconButton>
                    </CardActions>
                  )}
                  <IconWrapper>{getIconComponent(achievement.data?.icon)}</IconWrapper>
                  <AchievementTitle>{achievement.title}</AchievementTitle>
                  <AchievementDescription dangerouslySetInnerHTML={{ __html: achievement.description }} />
                </AchievementCard>
              ))
            )}
          </AchievementsGrid>
        </Section>

        {/* Current Courses */}
        <Section
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <SectionTitleWithButton>
            <SectionTitle>Current Courses</SectionTitle>
            {isAdmin && (
              <AddButton onClick={startCreateCourse}>
                + Add Course
              </AddButton>
            )}
          </SectionTitleWithButton>
          <CoursesGrid>
            {courses.map((course, index) => (
              <CourseCard
                key={course._id || index}
                as={motion.div}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                {isAdmin && (
                  <CardActions>
                    <EditIconButton onClick={() => startEditCourse(course)}>
                      <FiEdit2 />
                    </EditIconButton>
                    <DeleteIconButton onClick={() => handleCourseDelete(course._id)}>
                      <FaTrash />
                    </DeleteIconButton>
                  </CardActions>
                )}
                <CourseHeader>
                  <CourseCode>{course.code}</CourseCode>
                  <StatusBadge status={course.status}>{course.status}</StatusBadge>
                </CourseHeader>
                <CourseTitle>{course.title}</CourseTitle>
                <CourseDescription dangerouslySetInnerHTML={{ __html: course.description }} />
                <TopicsContainer>
                  <TopicsLabel>Topics Covered:</TopicsLabel>
                  <TopicTags>
                    {course.topics.map((topic, idx) => (
                      <TopicTag key={idx}>{topic}</TopicTag>
                    ))}
                  </TopicTags>
                </TopicsContainer>
                <CourseButton 
                  onClick={(e) => handleCourseClick(e, course.link || COURSE_IDS.ITWS1100)}
                  disabled={isAuthenticated && !courseAccess[course.link || COURSE_IDS.ITWS1100] && user.role !== 'admin'}
                >
                  {!isAuthenticated ? (
                    <>
                      <FiLock /> Login to Access Course
                    </>
                  ) : courseAccess[course.link || COURSE_IDS.ITWS1100] === false && user.role !== 'admin' ? (
                    <>
                      <FiLock /> Request Access
                    </>
                  ) : (
                    <>
                      Visit Course Site <FiExternalLink />
                    </>
                  )}
                </CourseButton>
              </CourseCard>
            ))}
          </CoursesGrid>
        </Section>

        {/* Additional Resources */}
        <Section
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <SectionTitleWithButton>
            <SectionTitle>{resources.title}</SectionTitle>
            {isAdmin && (
              <EditButton onClick={openResourcesModal}>
                <FiEdit2 /> Edit
              </EditButton>
            )}
          </SectionTitleWithButton>
          <ResourcesText dangerouslySetInnerHTML={{ __html: resources.description }} />
        </Section>
      </Container>

      {/* Achievement Edit Modal */}
      {showAchModal && (
        <EditableModal
          isOpen={showAchModal}
          onClose={cancelAchEdit}
          onSave={saveAch}
          title={isCreatingAch ? 'Add Achievement' : 'Edit Achievement'}
        >
          <FormGroup>
            <Label>Title *</Label>
            <Input 
              type="text" 
              value={localAchData.title || ''} 
              onChange={e => setLocalAchData({...localAchData, title: e.target.value})}
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Description *</Label>
            <RichTextEditor
              value={localAchData.description || ''} 
              onChange={(content) => setLocalAchData({...localAchData, description: content})}
              placeholder="Achievement description..."
              height="150px"
            />
          </FormGroup>
          <FormGroup>
            <Label>Icon</Label>
            <Select 
              value={localAchData.data?.icon || 'FiAward'} 
              onChange={e => setLocalAchData({...localAchData, data: {...localAchData.data, icon: e.target.value}})}
            >
              <option value="FiAward">Award</option>
              <option value="FiUsers">Users</option>
              <option value="FiBook">Book</option>
            </Select>
          </FormGroup>
        </EditableModal>
      )}

      {/* Course Edit Modal */}
      {showCourseModal && (
        <EditableModal
          isOpen={showCourseModal}
          onClose={() => {
            setShowCourseModal(false);
            setCurrentCourse(null);
          }}
          onSave={saveCourse}
          title={isCreatingCourse ? 'Add Course' : 'Edit Course'}
        >
          <FormGroup>
            <Label>Course Code *</Label>
            <Input 
              type="text" 
              value={courseForm.data?.code || ''} 
              onChange={e => setCourseForm({...courseForm, data: {...courseForm.data, code: e.target.value}})}
              placeholder="e.g., ITWS 1100" 
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Course Link</Label>
            <Input 
              type="text" 
              value={courseForm.data?.url || ''} 
              onChange={e => setCourseForm({...courseForm, data: {...courseForm.data, url: e.target.value}})}
              placeholder="e.g., rp001 or itws1100" 
            />
          </FormGroup>
          <FormGroup>
            <Label>Course Title *</Label>
            <Input 
              type="text" 
              value={courseForm.title || ''} 
              onChange={e => setCourseForm({...courseForm, title: e.target.value})}
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Description *</Label>
            <RichTextEditor
              value={courseForm.description || ''} 
              onChange={(content) => setCourseForm({...courseForm, description: content})}
              placeholder="Course description..."
              height="150px"
            />
          </FormGroup>
          <FormGroup>
            <Label>Status *</Label>
            <Input 
              type="text" 
              value={courseForm.data?.status || ''} 
              onChange={e => setCourseForm({...courseForm, data: {...courseForm.data, status: e.target.value}})}
              placeholder="e.g., Fall 2024, Active" 
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Topics (comma-separated)</Label>
            <Input 
              type="text" 
              value={Array.isArray(courseForm.data?.topics) ? courseForm.data.topics.join(', ') : ''} 
              onChange={e => setCourseForm({...courseForm, data: {...courseForm.data, topics: e.target.value.split(',').map(t => t.trim())}})}
              placeholder="e.g., HTML/CSS, JavaScript, PHP"
            />
          </FormGroup>
        </EditableModal>
      )}

      {/* Philosophy Edit Modal */}
      {showPhilosophyModal && (
        <EditableModal
          isOpen={showPhilosophyModal}
          onClose={() => setShowPhilosophyModal(false)}
          onSave={savePhilosophy}
          title="Edit Teaching Philosophy"
        >
          <FormGroup>
            <Label>Title *</Label>
            <Input 
              type="text" 
              value={philosophyForm.title || ''} 
              onChange={e => setPhilosophyForm({...philosophyForm, title: e.target.value})}
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Content *</Label>
            <RichTextEditor
              value={philosophyForm.description || ''} 
              onChange={(content) => setPhilosophyForm({...philosophyForm, description: content})}
              placeholder="Teaching philosophy content..."
              height="250px"
            />
          </FormGroup>
        </EditableModal>
      )}

      {/* Resources Edit Modal */}
      {showResourcesModal && (
        <EditableModal
          isOpen={showResourcesModal}
          onClose={() => setShowResourcesModal(false)}
          onSave={saveResources}
          title="Edit Student Resources"
        >
          <FormGroup>
            <Label>Title *</Label>
            <Input 
              type="text" 
              value={resourcesForm.title || ''} 
              onChange={e => setResourcesForm({...resourcesForm, title: e.target.value})}
              required 
            />
          </FormGroup>
          <FormGroup>
            <Label>Content *</Label>
            <RichTextEditor
              value={resourcesForm.description || ''} 
              onChange={(content) => setResourcesForm({...resourcesForm, description: content})}
              placeholder="Student resources content..."
              height="250px"
            />
          </FormGroup>
        </EditableModal>
      )}

      {/* Error Modal for Course Availability */}
      {errorModal.isOpen && (
        <ErrorModalOverlay onClick={() => setErrorModal({ ...errorModal, isOpen: false })}>
          <ErrorModalWrapper onClick={(e) => e.stopPropagation()}>
            <ErrorModalTitle>{errorModal.title}</ErrorModalTitle>
            <ErrorModalMessage>{errorModal.message}</ErrorModalMessage>
            <ErrorModalButton onClick={() => setErrorModal({ ...errorModal, isOpen: false })}>
              OK
            </ErrorModalButton>
          </ErrorModalWrapper>
        </ErrorModalOverlay>
      )}
    </TeachingContainer>
  );
};

const TeachingContainer = styled.div`
  --teaching-bg: #0b1220;
  --teaching-bg-alt: #0f1b2d;
  --teaching-surface: #121f33;
  --teaching-surface-strong: #182945;
  --teaching-border: rgba(148, 196, 255, 0.12);
  --teaching-text: #e6f0ff;
  --teaching-text-muted: #b3c7e6;
  --teaching-text-soft: #9ab3d1;
  --teaching-accent: #5fb2ff;
  --teaching-accent-strong: #3c7cff;
  --teaching-accent-glow: rgba(79, 139, 255, 0.4);
  --teaching-accent-gradient: linear-gradient(135deg, #2f6bff 0%, #5fb2ff 55%, #7ce7ff 100%);
  --teaching-chip-bg: rgba(95, 178, 255, 0.12);
  --teaching-input-bg: rgba(18, 31, 51, 0.9);
  --teaching-modal-bg: #12223a;
  --teaching-shadow: 0 12px 30px rgba(6, 16, 28, 0.5);
  --teaching-shadow-strong: 0 20px 45px rgba(6, 16, 28, 0.6);

  min-height: 100vh;
  padding: 6rem 0;
  background: radial-gradient(circle at top, #11233c 0%, var(--teaching-bg) 45%, #0a101b 100%);
  color: var(--teaching-text);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 15% 20%, rgba(95, 178, 255, 0.15), transparent 45%),
      radial-gradient(circle at 85% 10%, rgba(60, 124, 255, 0.2), transparent 35%),
      radial-gradient(circle at 70% 80%, rgba(124, 231, 255, 0.12), transparent 40%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const WelcomeMessage = styled.div`
  background: var(--teaching-accent-gradient);
  border-radius: 1rem;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 10px 30px rgba(20, 40, 80, 0.35);
  border: 1px solid rgba(159, 210, 255, 0.25);
`;

const WelcomeIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(10, 20, 40, 0.35);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #f5fbff;
  box-shadow: inset 0 0 12px rgba(255, 255, 255, 0.25);
`;

const WelcomeContent = styled.div`
  flex: 1;
`;

const WelcomeTitle = styled.h3`
  font-size: 1.5rem;
  color: #f5fbff;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 6px rgba(6, 16, 28, 0.4);
`;

const WelcomeText = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.92);
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--teaching-text);
  text-shadow: 0 8px 25px rgba(6, 16, 28, 0.5);
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  color: var(--teaching-text-soft);
`;

const Section = styled.section`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  background: var(--teaching-accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PhilosophyText = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--teaching-text-muted);
  max-width: 900px;
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const AchievementCard = styled.div`
  position: relative;
  background: var(--teaching-surface);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid var(--teaching-border);
  box-shadow: var(--teaching-shadow);
  transition: all 0.3s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--teaching-accent-gradient);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--teaching-shadow-strong);
    border-color: rgba(95, 178, 255, 0.5);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: var(--teaching-accent-gradient);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #f5fbff;
  margin-bottom: 1rem;
  box-shadow: 0 12px 25px rgba(20, 40, 80, 0.35);
`;

const AchievementTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: var(--teaching-text);
`;

const AchievementDescription = styled.p`
  color: var(--teaching-text-muted);
  line-height: 1.6;
`;

const CoursesGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const CourseCard = styled.div`
  position: relative;
  background: var(--teaching-surface);
  padding: 2.5rem;
  border-radius: 16px;
  border: 1px solid var(--teaching-border);
  box-shadow: var(--teaching-shadow);
  transition: all 0.3s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--teaching-accent-gradient);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--teaching-shadow-strong);
    border-color: rgba(95, 178, 255, 0.45);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const CourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CourseCode = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--teaching-accent);
  font-family: 'Space Grotesk', sans-serif;
`;

const StatusBadge = styled.span`
  padding: 0.375rem 0.875rem;
  background: ${props => {
    switch (props.status) {
      case 'Active':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'Pending':
        return 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'; // Orange
      case 'Coming Soon':
        return 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'; // Yellow
      case 'Inactive':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'; // Red
      default:
        return 'var(--bg-tertiary)';
    }
  }};
  color: white;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const CourseTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--teaching-text);
`;

const CourseDescription = styled.p`
  color: var(--teaching-text-muted);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const TopicsContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TopicsLabel = styled.p`
  font-weight: 600;
  color: var(--teaching-text);
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
`;

const TopicTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TopicTag = styled.span`
  padding: 0.375rem 0.875rem;
  background: var(--teaching-chip-bg);
  color: var(--teaching-text-soft);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid rgba(95, 178, 255, 0.2);
`;

const CourseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.disabled ? 'rgba(148, 196, 255, 0.1)' : 'var(--teaching-accent-gradient)'};
  color: #f5fbff;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  border: 1px solid rgba(159, 210, 255, 0.25);

  svg {
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const ResourcesText = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--teaching-text-muted);
  max-width: 800px;
`;

const SectionTitleWithButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: var(--teaching-accent-gradient);
  color: #f5fbff;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.9rem;
  border: 1px solid rgba(159, 210, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${AchievementCard}:hover &,
  ${CourseCard}:hover & {
    opacity: 1;
  }
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(95, 178, 255, 0.12);
  border: 1px solid rgba(95, 178, 255, 0.5);
  color: var(--teaching-accent);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--teaching-accent);
    color: #031025;
    transform: scale(1.05);
  }
`;

const DeleteIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.05);
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(95, 178, 255, 0.12);
  border: 1px solid rgba(95, 178, 255, 0.4);
  color: var(--teaching-accent);
  border-radius: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--teaching-accent);
    color: #031025;
    transform: translateY(-2px);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--teaching-text);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(148, 196, 255, 0.2);
  border-radius: 0.5rem;
  background: var(--teaching-input-bg);
  color: var(--teaching-text);
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(95, 178, 255, 0.8);
    box-shadow: 0 0 0 3px rgba(95, 178, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(148, 196, 255, 0.2);
  border-radius: 0.5rem;
  background: var(--teaching-input-bg);
  color: var(--teaching-text);
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(95, 178, 255, 0.8);
    box-shadow: 0 0 0 3px rgba(95, 178, 255, 0.2);
  }
`;

// Error Modal Styles
const ErrorModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(4, 8, 16, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ErrorModalWrapper = styled.div`
  background: var(--teaching-modal-bg);
  padding: 2rem;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 18px 45px rgba(3, 9, 18, 0.65);
  border: 1px solid rgba(95, 178, 255, 0.2);
`;

const ErrorModalTitle = styled.h3`
  margin-bottom: 1rem;
  color: var(--teaching-text);
  font-size: 1.5rem;
  font-weight: 600;
`;

const ErrorModalMessage = styled.p`
  margin-bottom: 1.5rem;
  color: var(--teaching-text-muted);
  line-height: 1.6;
  font-size: 1rem;
`;

const ErrorModalButton = styled.button`
  width: 100%;
  background: var(--teaching-accent-gradient);
  color: #f5fbff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-2px);
  }
`;

export default TeachingBlue;
